// PersonCard — the "Requested by" / "Member" card inside the task drawer.
// design ref: styles.css:1210 (.person / .person__head / .person__chips /
// .person__rows / .person__more) + drawers.jsx PersonCard.
//
// Renders only the fields the task-detail API actually returns (name, email,
// job title, department, location — plus, for the access-approval requester,
// manager name / employee status / start date). It does NOT fabricate the
// prototype's extra HRIS rows (team, business unit, role level, etc.).

import { Box, Flex, HStack, Icon, Stack, Text } from "@chakra-ui/react";
import { ReactNode, useState } from "react";
import { TbCirclePlus, TbCircleMinus } from "react-icons/tb";

import Avatar from "components/ds/Avatar";
import Chip from "components/ds/Chip";
import type { MFTaskMember } from "state/members/tasks";
import {
  formatDate,
  memberFullName,
  memberLocation,
  memberStatusMeta,
  resolveTaskPerson,
  type TaskPersonIdentity,
} from "./taskMeta";

// A chip with a muted "label:" prefix + emphasised value, at 14px to match the
// prototype's inlined font size on these chips.
const LabelChip = ({ label, value }: { label: string; value: string }) => (
  <Chip>
    <Box as="span" fontSize="md" color="neutral.700">
      {label}:
    </Box>
    <Box as="span" fontSize="md" fontWeight={500} color="neutral.950" ml="2xs">
      {value}
    </Box>
  </Chip>
);

const Row = ({ label, value }: { label: string; value: React.ReactNode }) => (
  <Flex
    align="center"
    minH="48px"
    py="sm"
    borderBottomWidth="1px"
    borderColor="neutral.200"
    fontSize="md"
    _last={{ borderBottom: "none" }}
  >
    <Box w="45%" color="neutral.700" fontWeight={500}>
      {label}
    </Box>
    <Box flex="1" color="neutral.950" fontWeight={500}>
      {value}
    </Box>
  </Flex>
);

export interface PersonCardProps {
  /** The member this card is about. Null for an NHI requester (see `identity`). */
  member: MFTaskMember | null | undefined;
  /**
   * NHI / service-account descriptor, used when there is no `member` (an access
   * request raised by a non-human identity). When both are absent the card shows
   * a generic "Non-human identity" fallback.
   */
  identity?: TaskPersonIdentity | null;
  /** Whether to surface the requester-only HR chips (status / manager / start). */
  showHrContext?: boolean;
  /**
   * Whether to show the "Started …" chip / Start date row. Defaults to true.
   * Offboarding passes false — the design shows a "Leaving …" chip (via
   * `extraChips`) in its place.
   */
  showStarted?: boolean;
  /** Trailing chip(s) for the collapsed row, e.g. a "Leaving …" chip. */
  extraChips?: ReactNode;
}

// Card shell shared by the member and identity variants (design: .person).
const Shell = ({ children }: { children: ReactNode }) => (
  <Box
    borderWidth="1px"
    borderColor="neutral.200"
    borderRadius="10px" // design: .person is 10px (no token at 10px — inline it)
    bg="neutral.25"
    p="lg" // 16px
  >
    {children}
  </Box>
);

// Humanise an identity's free-string type (e.g. "service_account" → "Service
// account") for the sub-line; fall back to a generic descriptor.
const identityTypeLabel = (type?: string | null): string => {
  if (!type) return "Non-human identity";
  const spaced = type.replace(/[_-]+/g, " ").trim();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
};

// The NHI / member-less requester variant: name + a "Service account" affordance,
// no HR context (an identity has no member HR fields to expand).
const IdentityCard = ({ identity }: { identity: TaskPersonIdentity | null }) => {
  const name = identity?.name || "Non-human identity";
  return (
    <Shell>
      <HStack spacing="md" mb="md">
        <Avatar name={name} size="lg" ring />
        <Box minW={0}>
          <Text fontSize="md" fontWeight={600} color="neutral.950" noOfLines={1}>
            {name}
          </Text>
          <Text fontSize="smd" color="neutral.700" noOfLines={1}>
            {identityTypeLabel(identity?.type)}
          </Text>
        </Box>
      </HStack>
      <Flex gap="1xs" flexWrap="wrap">
        <Chip dot="azurite">Service account</Chip>
      </Flex>
    </Shell>
  );
};

const PersonCard = ({
  member: memberProp,
  identity,
  showHrContext = false,
  showStarted = true,
  extraChips,
}: PersonCardProps) => {
  const [expanded, setExpanded] = useState(false);

  const person = resolveTaskPerson(memberProp, identity);
  if (person.kind !== "member") {
    return (
      <IdentityCard identity={person.kind === "identity" ? person.identity : null} />
    );
  }
  const member = person.member;

  const fullName = memberFullName(member);
  const location = memberLocation(member);
  const status = showHrContext ? memberStatusMeta(member.status) : null;
  const startDate =
    showHrContext && showStarted ? formatDate(member.employmentStartDate) : null;
  const manager = showHrContext ? member.managerName : null;

  // Rows shown when expanded — only the fields we actually have.
  const rows: { label: string; value: React.ReactNode }[] = [];
  if (status) rows.push({ label: "Status", value: <Chip dot={status.tone}>{status.label}</Chip> });
  if (member.jobTitle) rows.push({ label: "Job Title", value: <Chip>{member.jobTitle}</Chip> });
  if (member.department) rows.push({ label: "Department", value: <Chip>{member.department}</Chip> });
  if (manager) rows.push({ label: "Manager", value: <Chip>{manager}</Chip> });
  if (location) rows.push({ label: "Location", value: <Chip>{location}</Chip> });
  if (startDate) rows.push({ label: "Start date", value: <Text as="span">{startDate}</Text> });

  return (
    <Box
      borderWidth="1px"
      borderColor="neutral.200"
      borderRadius="10px" // design: .person is 10px (no token at 10px — inline it)
      bg="neutral.25"
      p="lg" // 16px
    >
      {/* .person__head */}
      <HStack spacing="md" mb="md">
        <Avatar name={fullName} src={member.imageUrl ?? undefined} size="lg" ring />
        <Box minW={0}>
          <Text fontSize="md" fontWeight={600} color="neutral.950" noOfLines={1}>
            {fullName}
          </Text>
          <Text fontSize="smd" color="neutral.700" noOfLines={1}>
            {member.email}
          </Text>
        </Box>
      </HStack>

      {!expanded ? (
        // .person__chips — collapsed chip row
        <Flex gap="1xs" flexWrap="wrap">
          {status && <Chip dot={status.tone}>{status.label}</Chip>}
          {member.jobTitle && <LabelChip label="Job Title" value={member.jobTitle} />}
          {member.department && <LabelChip label="Department" value={member.department} />}
          {manager && <LabelChip label="Manager" value={manager} />}
          {startDate && (
            <Chip dot="warning">
              <Box as="span" fontSize="md" color="neutral.950">
                Started {startDate}
              </Box>
            </Chip>
          )}
          {extraChips}
        </Flex>
      ) : (
        // .person__rows — expanded label/value table
        <Stack
          spacing={0}
          borderTopWidth="1px"
          borderColor="neutral.200"
          mx="-16px"
          px="lg"
        >
          {rows.map((r) => (
            <Row key={r.label} label={r.label} value={r.value} />
          ))}
        </Stack>
      )}

      {rows.length > 0 && (
        <Flex
          as="button"
          type="button"
          align="center"
          gap="1xs"
          mt="14px"
          fontSize="md" // design inlines 14px on .person__more (CSS base 12px is overridden)
          fontWeight={500}
          color="neutral.700"
          _hover={{ color: "neutral.900" }}
          onClick={() => setExpanded((v) => !v)}
        >
          <Icon as={expanded ? TbCircleMinus : TbCirclePlus} boxSize="16px" />
          {expanded ? "Show less" : "Show more"}
        </Flex>
      )}
    </Box>
  );
};

export default PersonCard;
